import React from 'react';
import styled from 'styled-components';
import { Poppins } from 'src/common/Poppins';
import { Tooltip } from 'src/common/Tooltip';
import { VariablePill } from 'src/common/VariablePill';
import { formatNumber } from 'src/utils/misc';
import { CarbonFlowSvg } from 'src/assets/dynamicSvg';
import { IntelVariable } from '../types';

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
`;

// Fixed height keeps it level with the constant pills in the same row.
const VarPill = styled(VariablePill)`
  height: 28px;
  white-space: nowrap;
`;

const Pill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 6px 12px;
  border-radius: 1000px;
  background: ${({ theme }) => theme.colors.cultured};
  white-space: nowrap;
`;

// #<id> | [label:value] | parens | operators | numbers
const TOKEN_RE = /#(\d+)|\[([^\]]*)\]|[()]|[*+/-]|\d+(?:\.\d+)?/g;

interface IntelFormulaViewProps {
  raw: string;
  variables: IntelVariable[];
  onVariableClick?: (variableId: number, anchorEl: HTMLElement) => void;
}

export const IntelFormulaView: React.FC<IntelFormulaViewProps> = ({ raw, variables, onVariableClick }) => {
  const varById = new Map(variables.map((v) => [v.id, v]));
  const tokens: React.ReactNode[] = [];

  let match: RegExpExecArray | null;
  let key = 0;
  // eslint-disable-next-line no-cond-assign
  while ((match = TOKEN_RE.exec(raw)) !== null) {
    if (match[1] !== undefined) {
      const id = Number(match[1]);
      const variable = varById.get(id);
      const populated = !!variable && variable.value != null;
      const tip = populated
        ? formatNumber(variable!.value as number, { symbol: variable!.currency, maxDecimals: 3 })
        : 'No Data';
      // Only clickable when the variable exists; a `#<id>` with no backing variable must not open an empty editor.
      const clickable = !!variable && variable.value == null && !!onVariableClick;
      tokens.push(
        <Tooltip key={key} dataId={`ai-var-${id}-${key}`} dataHtml={tip} place="top" inline>
          <VarPill
            unpopulated={!populated}
            bordered={false}
            radius={1000}
            padding="6px 12px"
            gap={10}
            icon={<CarbonFlowSvg size={14} />}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            style={clickable ? { cursor: 'pointer' } : undefined}
            onClick={clickable ? (e: React.MouseEvent<HTMLElement>) => onVariableClick!(id, e.currentTarget) : undefined}
          >
            <Poppins px={14} weight={500} color="prussianBlue">
              {variable?.name ?? `#${id}`}
            </Poppins>
          </VarPill>
        </Tooltip>,
      );
    } else if (match[2] !== undefined) {
      const content = match[2];
      const sep = content.lastIndexOf(':');
      // Tokens carry spaces around the colon (`[disruption days : 5]`); trim so the chip reads "label: 5".
      const label = (sep >= 0 ? content.slice(0, sep) : content).trim();
      const value = (sep >= 0 ? content.slice(sep + 1) : '').trim();
      tokens.push(
        <Pill key={key}>
          <Poppins px={14} color="cflowerBlue">
            {label}:
          </Poppins>
          <Poppins px={14} weight={500} color="prussianBlue">
            {value}
          </Poppins>
        </Pill>,
      );
    } else if (/^\d/.test(match[0])) {
      tokens.push(
        <Poppins key={key} px={14} weight={500} color="prussianBlue">
          {match[0]}
        </Poppins>,
      );
    } else {
      const op = match[0];
      const small = op === '+' || op === '-';
      tokens.push(
        <Poppins key={key} px={small ? 14 : 20} color="cflowerBlue">
          {op}
        </Poppins>,
      );
    }
    key += 1;
  }

  return <Row>{tokens}</Row>;
};
